import React, { useState, useContext, useCallback } from 'react';
import { AuthContext } from '../../App';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';
import { 
  ArrowRight, 
  CheckCircle2, 
  Loader2,
  AlertTriangle
} from 'lucide-react';

import InspectionUploadDropzone from './InspectionUploadDropzone';
import InspectionFilePreviewList from './InspectionFilePreviewList';
import ExtractionProgressPanel from './ExtractionProgressPanel';
import ExtractedFieldsReview from './ExtractedFieldsReview';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * InspectionImportTab - Main tab component for OCR inspection import
 * 
 * Tab 3 in Create New Inspection modal
 */
export default function InspectionImportTab({ 
  onInspectionCreated,
  onCancel,
  existingInspectionId = null
}) {
  const { token } = useContext(AuthContext);
  
  // State
  const [files, setFiles] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [step, setStep] = useState('upload'); // upload, processing, review, complete
  const [progress, setProgress] = useState(0);
  const [extractedFields, setExtractedFields] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Add files from dropzone
  const handleFilesAdded = useCallback((newFiles) => {
    setFiles(prev => [...prev, ...newFiles].slice(0, 20));
    setError(null);
  }, []);
  
  // Remove file
  const handleRemoveFile = useCallback((index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }, []);
  
  // Reorder files
  const handleReorderFiles = useCallback((fromIndex, toIndex) => {
    setFiles(prev => {
      const result = [...prev];
      const [removed] = result.splice(fromIndex, 1);
      result.splice(toIndex, 0, removed);
      return result;
    });
  }, []);
  
  // Upload files and start processing
  const handleStartExtraction = async () => {
    if (files.length === 0) {
      toast.error('Please add at least one file');
      return;
    }
    
    setLoading(true);
    setStep('processing');
    setProgress(10);
    setError(null);
    
    try {
      // Step 1: Upload files
      const formData = new FormData();
      files.forEach(f => formData.append('files', f));
      if (existingInspectionId) {
        formData.append('inspection_id', existingInspectionId);
      }
      
      setProgress(20);
      
      const uploadRes = await fetch(`${API_URL}/api/inspection-import/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      const uploadData = await uploadRes.json();
      
      if (!uploadData.success) {
        throw new Error(uploadData.detail || 'Upload failed');
      }
      
      setJobId(uploadData.job_id);
      setProgress(40);
      
      // Step 2: Process (OCR + extraction)
      const processRes = await fetch(
        `${API_URL}/api/inspection-import/${uploadData.job_id}/process`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      setProgress(80);
      
      const processData = await processRes.json();
      
      if (!processData.success) {
        throw new Error(processData.detail || 'Processing failed');
      }
      
      setProgress(100);
      setExtractedFields(processData.fields);
      setConflicts(processData.conflicts || []);
      setStep('review');
      
      toast.success(`Extracted ${processData.fields_extracted} fields from ${files.length} file(s)`);
      
    } catch (err) {
      setError(err.message);
      setStep('upload');
      toast.error(`Extraction failed: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Confirm extraction and create/update inspection
  const handleConfirmExtraction = async (confirmedFields) => {
    if (!jobId) {
      toast.error('No import job found');
      return;
    }
    
    setLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/inspection-import/${jobId}/confirm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          fields: confirmedFields,
          inspection_id: existingInspectionId,
          overwrite_existing: false
        })
      });
      
      const data = await res.json();
      
      if (!data.success) {
        throw new Error(data.detail || 'Confirmation failed');
      }
      
      setStep('complete');
      toast.success(`Inspection populated with ${data.fields_populated} fields`);
      
      if (onInspectionCreated) {
        onInspectionCreated(data.inspection_id);
      }
      
    } catch (err) {
      toast.error(`Failed to save: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Reset to start over
  const handleReset = () => {
    setFiles([]);
    setJobId(null);
    setStep('upload');
    setProgress(0);
    setExtractedFields(null);
    setConflicts([]);
    setError(null);
  };
  
  return (
    <div className="space-y-6" data-testid="inspection-import-tab">
      {/* Step indicator */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          <Badge variant={step === 'upload' ? 'default' : 'secondary'}>1. Upload</Badge>
          <ArrowRight className="h-4 w-4 text-gray-400" />
          <Badge variant={step === 'processing' ? 'default' : 'secondary'}>2. Process</Badge>
          <ArrowRight className="h-4 w-4 text-gray-400" />
          <Badge variant={step === 'review' ? 'default' : 'secondary'}>3. Review</Badge>
          <ArrowRight className="h-4 w-4 text-gray-400" />
          <Badge variant={step === 'complete' ? 'default' : 'secondary'}>4. Complete</Badge>
        </div>
      </div>
      
      <Separator />
      
      {/* Upload Step */}
      {step === 'upload' && (
        <div className="space-y-4">
          <InspectionUploadDropzone onFilesAdded={handleFilesAdded} />
          
          {files.length > 0 && (
            <>
              <InspectionFilePreviewList
                files={files}
                onRemove={handleRemoveFile}
                onReorder={handleReorderFiles}
              />
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={onCancel}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleStartExtraction}
                  disabled={loading}
                  data-testid="start-extraction-btn"
                >
                  {loading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Extract Fields ({files.length} file{files.length !== 1 ? 's' : ''})
                </Button>
              </div>
            </>
          )}
          
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-700">Error</p>
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Processing Step */}
      {step === 'processing' && (
        <ExtractionProgressPanel
          progress={progress}
          fileCount={files.length}
          currentFile={Math.ceil((progress / 100) * files.length) || 1}
        />
      )}
      
      {/* Review Step */}
      {step === 'review' && extractedFields && (
        <ExtractedFieldsReview
          fields={extractedFields}
          conflicts={conflicts}
          onConfirm={handleConfirmExtraction}
          onCancel={handleReset}
          loading={loading}
          jobId={jobId}
        />
      )}
      
      {/* Complete Step */}
      {step === 'complete' && (
        <div className="text-center py-8">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Import Complete
          </h3>
          <p className="text-gray-600 mb-6">
            Your inspection has been populated with the extracted data.
          </p>
          <Button onClick={onCancel}>
            Close
          </Button>
        </div>
      )}
    </div>
  );
}
